import { useQuery } from '@tanstack/react-query';
import { requireSupabase, isSupabaseConfigured } from '@/lib/supabase/client';
import type {
  City,
  Feature,
  LocationRow,
  OptionCatalogRow,
  PlatformSettings,
  VehicleCategory,
} from '@/lib/supabase/database.types';

/**
 * Référentiels de la plateforme.
 *
 * Ces données changent rarement : on les met en cache une heure. Elles viennent
 * toutes de la base, jamais de constantes codées en dur — c'est ce qui rend la
 * commission, les villes, les catégories et les options modifiables depuis le
 * back-office sans redéploiement.
 */

const HOUR = 60 * 60 * 1000;

export function usePlatformSettings() {
  return useQuery({
    queryKey: ['platform', 'settings'],
    enabled: isSupabaseConfigured,
    staleTime: HOUR,
    queryFn: async (): Promise<PlatformSettings> => {
      const { data, error } = await requireSupabase()
        .from('platform_settings')
        .select('*')
        .eq('id', 1)
        .single();
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useCities(onlyActive = false) {
  return useQuery({
    queryKey: ['platform', 'cities', onlyActive],
    enabled: isSupabaseConfigured,
    staleTime: HOUR,
    queryFn: async (): Promise<City[]> => {
      let query = requireSupabase().from('cities').select('*').order('sort_order');
      if (onlyActive) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export function useCity(slug: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'city', slug],
    enabled: isSupabaseConfigured && Boolean(slug),
    staleTime: HOUR,
    queryFn: async (): Promise<City | null> => {
      const { data, error } = await requireSupabase()
        .from('cities')
        .select('*')
        .eq('slug', slug!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data;
    },
  });
}

export function useVehicleCategories() {
  return useQuery({
    queryKey: ['platform', 'categories'],
    enabled: isSupabaseConfigured,
    staleTime: HOUR,
    queryFn: async (): Promise<VehicleCategory[]> => {
      const { data, error } = await requireSupabase()
        .from('vehicle_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export function useFeatures() {
  return useQuery({
    queryKey: ['platform', 'features'],
    enabled: isSupabaseConfigured,
    staleTime: HOUR,
    queryFn: async (): Promise<Feature[]> => {
      const { data, error } = await requireSupabase()
        .from('features')
        .select('*')
        .order('sort_order');
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export function useOptionCatalog() {
  return useQuery({
    queryKey: ['platform', 'options'],
    enabled: isSupabaseConfigured,
    staleTime: HOUR,
    queryFn: async (): Promise<OptionCatalogRow[]> => {
      const { data, error } = await requireSupabase()
        .from('option_catalog')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export function useLocations(cityId: string | undefined) {
  return useQuery({
    queryKey: ['platform', 'locations', cityId],
    enabled: isSupabaseConfigured && Boolean(cityId),
    staleTime: HOUR,
    queryFn: async (): Promise<LocationRow[]> => {
      const { data, error } = await requireSupabase()
        .from('locations')
        .select('*')
        .eq('city_id', cityId!)
        .eq('is_active', true)
        .order('label');
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}
